import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuranPage = () => {
  const [surahs, setSurahs] = useState([]);
  const [selectedSurah, setSelectedSurah] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const fetchSurahs = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/quran/surah`);
        setSurahs(data);
      } catch (error) {
        console.error('Error fetching surahs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSurahs();
  }, []);

  const openSurah = async (nomor) => {
    try {
      setDetailLoading(true);
      const { data } = await axios.get(`${API_URL}/api/quran/surah/${nomor}`);
      setSelectedSurah(data);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error fetching surah detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  if (selectedSurah) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-[#F8F9FA] pb-24">
        <div className="sticky top-0 z-10 backdrop-blur-xl bg-white/70 border-b border-white/40 shadow-sm px-6 py-4">
          <button
            data-testid="back-to-surahs-btn"
            onClick={() => setSelectedSurah(null)}
            className="text-sm font-body text-emerald-700 font-medium mb-2"
          >
            ← Kembali ke Daftar Surah
          </button>
          <h2 className="text-2xl font-heading font-bold text-primary-dark">
            {selectedSurah.namaLatin}
          </h2>
          <p className="text-sm font-body text-text-secondary">
            {selectedSurah.arti} • {selectedSurah.jumlahAyat} Ayat • {selectedSurah.tempatTurun}
          </p>
        </div>

        <div className="px-6 py-6 space-y-6">
          {selectedSurah.nomor !== 1 && selectedSurah.nomor !== 9 && (
            <div className="text-center py-6">
              <p className="text-3xl font-arabic text-emerald-800">
                بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
              </p>
            </div>
          )}

          {selectedSurah.ayat.map((ayah, index) => (
            <motion.div
              key={ayah.nomorAyat}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.02 * index }}
              className="bg-white rounded-3xl shadow-sm border border-stone-100 p-6"
              data-testid={`ayah-${ayah.nomorAyat}`}
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                  <span className="text-sm font-body font-bold text-emerald-700">
                    {ayah.nomorAyat}
                  </span>
                </div>
              </div>
              <p className="text-3xl font-arabic leading-loose text-right text-text-primary mb-4">
                {ayah.teksArab}
              </p>
              <p className="text-sm font-body italic text-emerald-700/90 leading-relaxed mb-3" data-testid={`ayah-latin-${ayah.nomorAyat}`}>
                {ayah.teksLatin}
              </p>
              <p className="text-sm font-body text-text-secondary leading-relaxed border-t border-stone-100 pt-3" data-testid={`ayah-translation-${ayah.nomorAyat}`}>
                {ayah.teksIndonesia}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-[#F8F9FA] px-6 py-8 pb-24">
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold tracking-tight text-primary-dark mb-2">
          Al-Quran
        </h1>
        <p className="text-sm font-body text-text-secondary">
          Sumber data Kemenag RI • Arab, Latin & Terjemahan Indonesia
        </p>
      </div>

      {loading || detailLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-200 border-t-emerald-700" />
        </div>
      ) : (
        <div className="space-y-3">
          {surahs.map((surah, index) => (
            <motion.button
              key={surah.nomor}
              data-testid={`surah-${surah.nomor}`}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.01 * index }}
              onClick={() => openSurah(surah.nomor)}
              className="w-full bg-white rounded-2xl shadow-sm border border-stone-100 p-4 flex items-center justify-between hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-body font-bold text-emerald-700">
                    {surah.nomor}
                  </span>
                </div>
                <div className="text-left">
                  <h3 className="text-base font-body font-semibold text-text-primary">
                    {surah.namaLatin}
                  </h3>
                  <p className="text-xs font-body text-text-secondary">
                    {surah.arti} • {surah.jumlahAyat} Ayat
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xl font-arabic text-emerald-800">
                  {surah.nama}
                </span>
                <ChevronRight className="w-5 h-5 text-stone-400" />
              </div>
            </motion.button>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default QuranPage;
